import { CountryCodes, Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import type { IUISupplier } from '@bridebook/toolbox/src/types';
import { TrackingCategory } from '@bridebook/web/app-basic/lib/analytics/tracking-category';
import { TrackingEvent } from '@bridebook/web/app-basic/lib/analytics/tracking-event';
import { BasicStore, ThunkAction } from '@bridebook/web/app-basic/lib/types';
import { buildAnalyticsActiveFilters } from '@bridebook/web/lib/search/analytics-builders/build-active-filters';
import { analyticsCategory } from '@bridebook/web/lib/search/analytics-constants';

interface ViewedSupplierInSearchParams {
  supplier: IUISupplier;
  pagePosition: number;
  searchPosition: number;
  // fallback when the search response carries no country (mirrors logged-in market fallback)
  countryCode: CountryCodes;
}

/**
 * Builds the "Viewed supplier in search" payload for logged-out search.
 * Populates the subset of properties attainable without an authenticated user
 * and mirrors the logged-in triggering logic. Logged-in-only properties are
 * omitted rather than sent blank.
 */
export const buildViewedSupplierInSearchLoggedOutEvent = (
  {
    supplier,
    pagePosition,
    searchPosition,
    countryCode: marketCountryCode,
  }: ViewedSupplierInSearchParams,
  state: BasicStore,
) => {
  const fields = state.search.fields;
  const filters = state.search.filters;
  const previousPath = state.app.previousPath;
  const searchSource = state.searchBarInfo.source;

  const countryCode = fields?.countryCode ?? marketCountryCode;
  const countryName = Gazetteer.getCountryName(countryCode);

  const supplierCategory = fields?.type ?? state.search.actualCategory;
  const isVenueSearch = supplierCategory === 'venue';

  return {
    event: TrackingEvent.ViewedSupplierInSearch,
    category: analyticsCategory,
    loggedIn: false,
    supplierId: supplier.id,
    placeId: fields?.placeId,
    previousPath,
    searchActiveFilters: buildAnalyticsActiveFilters(filters, fields ?? null),
    searchCoords: {
      swlat: fields?.swlat,
      swlon: fields?.swlon,
      nelat: fields?.nelat,
      nelon: fields?.nelon,
    },
    searchPageNumber: fields?.page,
    searchSortedBy: fields?.sort,
    searchSource: searchSource || 'newSearch',
    searchTerm: fields?.area,
    searchType: fields?.searchType ?? '',
    // logged-out search only renders the list view
    searchView: 'list',
    supplierCategory,
    supplierCountryCode: countryCode,
    supplierCountryName: countryName,
    supplierScore: supplier._score ?? 0,
    supplierRegistered: Boolean(supplier.registrationDate),
    supplierTier: supplier.tier ?? 0,
    isForeign: Boolean(supplier.isForeign),
    ...(isVenueSearch
      ? {
          isTopSpotCounty: Boolean(
            supplier.supplierBoosted && supplier.boost && !supplier.isForeign,
          ),
          isTopSpotCountry: supplier.matchedBy?.includes('placeTopSpot') || false,
          isTopSpotVenueType: Boolean(
            supplier.searchBoostForType &&
              filters?.venueType?.[supplier.searchBoostForType] &&
              !supplier.isForeign,
          ),
        }
      : {}),
    pagePosition,
    searchPosition,
    trackingTarget: 'BigQuery' as const,
  };
};

/**
 * Tracks a "Viewed supplier in search" event for logged-out search when a
 * supplier tile becomes fully visible in the viewport.
 */
export const viewedSupplierInSearch =
  (params: ViewedSupplierInSearchParams): ThunkAction<void> =>
  (_, getState, { analyticsService }) => {
    analyticsService
      .getMethods(TrackingCategory.LoggedOut)
      .track(buildViewedSupplierInSearchLoggedOutEvent(params, getState()));
  };
